import { useState, useEffect, useRef } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { TOP_LANGUAGES, ALL_LANGUAGES } from "../data/languages";
import { ArrowLeftRight, Loader2, Star, Volume2, VolumeX, Copy, X, Mic, MicOff, Zap, MessagesSquare, Megaphone } from "lucide-react";
import { Switch } from "./ui/switch";
import ConversationMode from "./ConversationMode";

export default function Translator({ user, presetText, onPresetConsumed, activePatient, onSaveSession }) {
  const [mode, setMode] = useState(() => {
    if (typeof window === "undefined") return "single";
    return localStorage.getItem("tt_mode") || "single";
  });
  useEffect(() => {
    localStorage.setItem("tt_mode", mode);
  }, [mode]);
  const [sourceLang, setSourceLang] = useState("English");
  const [targetLang, setTargetLang] = useState("Spanish");
  const [text, setText] = useState("");
  const [translation, setTranslation] = useState("");
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [autoTranslate, setAutoTranslate] = useState(() => {
    if (typeof window === "undefined") return true;
    const v = localStorage.getItem("tt_auto_translate");
    return v === null ? true : v === "1";
  });
  const [voice, setVoice] = useState(() => {
    if (typeof window === "undefined") return "nova";
    return localStorage.getItem("tt_voice") || "nova";
  });
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const audioRef = useRef(null);

  // Persist auto-translate preference
  useEffect(() => {
    localStorage.setItem("tt_auto_translate", autoTranslate ? "1" : "0");
  }, [autoTranslate]);

  // Persist voice preference
  useEffect(() => {
    localStorage.setItem("tt_voice", voice);
  }, [voice]);

  // When an active patient is set, jump target language to their preferred language
  useEffect(() => {
    if (activePatient?.preferred_language) {
      setTargetLang(activePatient.preferred_language);
    }
  }, [activePatient?.id, activePatient?.preferred_language]);

  // Apply preset text from phrase library / favorites
  useEffect(() => {
    if (presetText) {
      setText(presetText);
      setTranslation("");
      onPresetConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetText]);

  const swap = () => {
    setSourceLang(targetLang);
    setTargetLang(sourceLang);
    setText(translation || "");
    setTranslation(text || "");
  };

  const runTranslate = async (overrideText) => {
    const src = (overrideText ?? text).trim();
    if (!src) {
      toast.error("Type something to translate");
      return null;
    }
    setBusy(true);
    setTranslation("");
    try {
      const { data } = await api.post("/translate", {
        text: src,
        source_lang: sourceLang,
        target_lang: targetLang,
        discipline: user.discipline,
      });
      setTranslation(data.translation);
      return data.translation;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Translation failed");
      return null;
    } finally {
      setBusy(false);
    }
  };

  const translate = () => runTranslate();

  const copy = () => {
    if (!translation) return;
    navigator.clipboard.writeText(translation);
    toast.success("Copied to clipboard");
  };

  const speakBrowser = () => {
    if (typeof window === "undefined" || !("speechSynthesis" in window)) return false;
    const utter = new SpeechSynthesisUtterance(translation);
    const langMap = { Spanish: "es-ES", Mandarin: "zh-CN", French: "fr-FR", German: "de-DE", Italian: "it-IT", Japanese: "ja-JP", Korean: "ko-KR", Russian: "ru-RU", Portuguese: "pt-PT", Hindi: "hi-IN", Arabic: "ar-SA", Vietnamese: "vi-VN" };
    utter.lang = langMap[targetLang] || "en-US";
    utter.rate = 0.9;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utter);
    return true;
  };

  const stopSpeaking = () => {
    try {
      audioRef.current?.pause();
      audioRef.current = null;
    } catch {}
    if (typeof window !== "undefined" && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    setSpeaking(false);
  };

  const speak = async () => {
    if (!translation) {
      toast.error("Nothing to speak yet");
      return;
    }
    // If already speaking, stop.
    if (speaking) {
      stopSpeaking();
      return;
    }
    setSpeaking(true);
    try {
      const res = await api.post(
        "/tts",
        { text: translation, voice, speed: 0.95 },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => {
        setSpeaking(false);
        URL.revokeObjectURL(url);
      };
      audio.onerror = () => {
        setSpeaking(false);
        URL.revokeObjectURL(url);
      };
      await audio.play();
    } catch (err) {
      // Fallback to browser TTS if backend TTS fails (budget, network, etc.)
      const ok = speakBrowser();
      if (!ok) toast.error("Speech not available on this device");
      // Reset after browser TTS finishes — best-effort timer
      setTimeout(() => setSpeaking(false), Math.max(2000, translation.length * 60));
    }
  };

  const saveFavorite = async () => {
    try {
      await api.post("/favorites", {
        text,
        translation,
        source_lang: sourceLang,
        target_lang: targetLang,
      });
      toast.success("Saved to favorites");
    } catch {
      toast.error("Could not save");
    }
  };

  const clear = () => {
    setText("");
    setTranslation("");
  };

  // ---- Voice input via Whisper ----
  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      try {
        recorderRef.current?.state === "recording" && recorderRef.current.stop();
      } catch {}
      stopStream();
    };
  }, []);

  const startRecording = async () => {
    if (typeof window === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      toast.error("Microphone not supported on this device");
      return;
    }
    if (!window.MediaRecorder) {
      toast.error("Audio recording not supported");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeOptions = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"];
      const mimeType = mimeOptions.find((m) => window.MediaRecorder.isTypeSupported?.(m)) || "";
      const rec = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        stopStream();
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        chunksRef.current = [];
        if (blob.size === 0) {
          toast.error("No audio captured");
          return;
        }
        await sendAudio(blob);
      };
      rec.start();
      recorderRef.current = rec;
      setRecording(true);
    } catch (err) {
      toast.error("Microphone permission denied");
      stopStream();
    }
  };

  const stopRecording = () => {
    try {
      if (recorderRef.current && recorderRef.current.state === "recording") {
        recorderRef.current.stop();
      }
    } catch {}
    setRecording(false);
  };

  const sendAudio = async (blob) => {
    setTranscribing(true);
    try {
      const ext = blob.type.includes("mp4") ? "mp4" : blob.type.includes("ogg") ? "ogg" : "webm";
      const fd = new FormData();
      fd.append("audio", blob, `recording.${ext}`);
      fd.append("language", sourceLang);
      const { data } = await api.post("/transcribe", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const transcript = (data.text || "").trim();
      if (!transcript) {
        toast.error("Could not understand audio. Please try again.");
        return;
      }
      setText(transcript);
      setTranslation("");
      toast.success("Transcribed");
      if (autoTranslate) {
        // Kick off translation immediately, passing transcript directly
        // (state update from setText hasn't flushed yet)
        runTranslate(transcript);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Transcription failed");
    } finally {
      setTranscribing(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="translator-pane">
      {/* Mode toggle */}
      <div className="inline-flex items-center gap-1 p-1 bg-white border border-zinc-200 rounded-lg" data-testid="translator-mode-toggle">
        <button
          onClick={() => setMode("single")}
          className={`h-10 px-4 rounded-md text-sm font-medium transition-colors duration-200 flex items-center gap-2 ${
            mode === "single" ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-50"
          }`}
          data-testid="mode-single"
        >
          <Megaphone className="w-4 h-4" /> Broadcast
        </button>
        <button
          onClick={() => setMode("conversation")}
          className={`h-10 px-4 rounded-md text-sm font-medium transition-colors duration-200 flex items-center gap-2 ${
            mode === "conversation" ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-50"
          }`}
          data-testid="mode-conversation"
        >
          <MessagesSquare className="w-4 h-4" /> Conversation
        </button>
      </div>

      {mode === "conversation" ? (
        <ConversationMode user={user} voice={voice} onSaveSession={onSaveSession} />
      ) : (
        <div className="space-y-6" data-testid="single-phrase-pane">
      {/* Quick language picker */}
      <div>
        <p className="text-sm uppercase tracking-[0.2em] text-zinc-500 mb-3">Quick select target language</p>
        <div className="flex flex-wrap gap-2" data-testid="quick-lang-row">
          {TOP_LANGUAGES.map((lang) => (
            <button
              key={lang.name}
              onClick={() => setTargetLang(lang.name)}
              className={`px-4 h-11 rounded-lg border text-sm font-medium transition-colors duration-200 ${
                targetLang === lang.name
                  ? "bg-zinc-900 text-white border-zinc-900"
                  : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"
              }`}
              data-testid={`quick-lang-${lang.name}`}
            >
              <span className="text-xs text-zinc-400 mr-2 font-mono">{lang.flag}</span>
              {lang.name}
              <span className="ml-2 text-zinc-400">{lang.native}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Source / target row */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] items-end gap-4">
        <div>
          <p className="text-sm uppercase tracking-[0.2em] text-zinc-500 mb-2">From</p>
          <Select value={sourceLang} onValueChange={setSourceLang}>
            <SelectTrigger className="h-12 text-base" data-testid="source-lang-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-80">
              {ALL_LANGUAGES.map((l) => (
                <SelectItem key={l} value={l}>{l}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          variant="outline"
          onClick={swap}
          className="h-12 w-12 p-0 mb-0"
          data-testid="swap-lang-button"
          title="Swap languages"
        >
          <ArrowLeftRight className="w-4 h-4" />
        </Button>
        <div>
          <p className="text-sm uppercase tracking-[0.2em] text-zinc-500 mb-2">To</p>
          <Select value={targetLang} onValueChange={setTargetLang}>
            <SelectTrigger className="h-12 text-base" data-testid="target-lang-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-80">
              {ALL_LANGUAGES.map((l) => (
                <SelectItem key={l} value={l}>{l}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Input */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm uppercase tracking-[0.2em] text-zinc-500">Source text</p>
          {text && (
            <button onClick={clear} className="text-xs text-zinc-500 hover:text-zinc-900 flex items-center gap-1" data-testid="clear-text-button">
              <X className="w-3 h-3" /> Clear
            </button>
          )}
        </div>
        <div className="relative">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={recording ? "Listening… speak now, then tap mic to stop." : "Type, paste, or tap the mic to dictate."}
            rows={4}
            className="text-lg resize-none border-zinc-200 pr-14"
            data-testid="source-text-input"
            disabled={transcribing}
          />
          <button
            type="button"
            onClick={recording ? stopRecording : startRecording}
            disabled={transcribing}
            className={`absolute right-3 bottom-3 w-10 h-10 rounded-full flex items-center justify-center transition-colors duration-200 ${
              recording
                ? "bg-red-600 text-white animate-pulse"
                : transcribing
                ? "bg-zinc-200 text-zinc-500"
                : "bg-zinc-900 text-white hover:bg-zinc-800"
            }`}
            title={recording ? "Stop recording" : "Dictate (Whisper)"}
            data-testid="mic-button"
          >
            {transcribing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : recording ? (
              <MicOff className="w-4 h-4" />
            ) : (
              <Mic className="w-4 h-4" />
            )}
          </button>
        </div>
        <div className="flex items-center justify-between gap-3 mt-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Button onClick={translate} disabled={busy || transcribing || recording} className="h-12 px-6 text-base" data-testid="translate-button">
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Translating</> : "Translate"}
            </Button>
            {(recording || transcribing) && (
              <span className="text-sm text-zinc-500" data-testid="mic-status">
                {recording ? "Recording…" : "Transcribing…"}
              </span>
            )}
          </div>
          <label className="flex items-center gap-2.5 cursor-pointer select-none" data-testid="auto-translate-toggle-wrap">
            <Zap className={`w-4 h-4 ${autoTranslate ? "text-zinc-900" : "text-zinc-400"}`} />
            <span className="text-sm text-zinc-700">Auto-translate after dictation</span>
            <Switch
              checked={autoTranslate}
              onCheckedChange={setAutoTranslate}
              data-testid="auto-translate-toggle"
            />
          </label>
        </div>
      </div>

      {/* Output — patient-facing big text */}
      <div className="border border-zinc-200 rounded-xl bg-zinc-50 p-6 md:p-8 min-h-[200px] flex flex-col" data-testid="translation-output-card">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <p className="text-sm uppercase tracking-[0.2em] text-zinc-500">{targetLang}</p>
          <div className="flex gap-2 items-center">
            <Select value={voice} onValueChange={setVoice}>
              <SelectTrigger className="h-9 w-[140px] text-xs" data-testid="voice-select" title="Choose patient-facing voice">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="nova">Nova · warm</SelectItem>
                <SelectItem value="alloy">Alloy · neutral</SelectItem>
                <SelectItem value="echo">Echo · calm</SelectItem>
                <SelectItem value="shimmer">Shimmer · bright</SelectItem>
                <SelectItem value="onyx">Onyx · deep</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={speak}
              disabled={!translation}
              data-testid="speak-translation-button"
              title={speaking ? "Stop" : "Speak (high-quality voice)"}
            >
              {speaking ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={copy}
              disabled={!translation}
              data-testid="copy-translation-button"
            >
              <Copy className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={saveFavorite}
              disabled={!translation}
              data-testid="favorite-translation-button"
            >
              <Star className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div className="flex-1 flex items-center">
          {translation ? (
            <p className="font-heading text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight text-zinc-900 break-words" data-testid="translation-output-text">
              {translation}
            </p>
          ) : (
            <p className="text-zinc-400 text-lg">Translation will appear here in large text for your patient to read.</p>
          )}
        </div>
      </div>
        </div>
      )}
    </div>
  );
}
